import React, { useEffect, useState, useCallback } from 'react';
import { sb } from '../../services/supabase';
import { PageHeader, Card, Table, Td, EmptyRow, Btn, Badge } from '../../components/ui';

export function CompanyReports({ companyId }: { companyId: string }) {
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoursMode, setHoursMode] = useState<'total' | 'sessions'>('total');
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
  });
  const [to, setTo] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });

  const loadRecords = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      // Step 1: Get all user IDs for this company
      const { data: companyUsers } = await sb
        .from('users')
        .select('id, full_name, employee_code')
        .eq('company_id', companyId);

      if (!companyUsers || companyUsers.length === 0) {
        setRecords([]); setLoading(false); return;
      }

      const ids = companyUsers.map((u: any) => u.id);
      const userMap: Record<string, any> = {};
      companyUsers.forEach((u: any) => { userMap[u.id] = u; });

      // Step 2: Get attendance records
      const { data: attendance, error } = await sb
        .from('attendance')
        .select('*')
        .in('user_id', ids)
        .gte('date', from)
        .lte('date', to)
        .order('date', { ascending: false });

      if (error) { console.error(error); setLoading(false); return; }

      // Step 3: Get attendance logs for these records
      const attIds = (attendance || []).map((a: any) => a.id);
      let logsMap: Record<string, any[]> = {};

      if (attIds.length > 0) {
        const { data: logs } = await sb
          .from('attendance_logs')
          .select('*')
          .in('attendance_id', attIds)
          .order('timestamp', { ascending: true });

        (logs || []).forEach((l: any) => {
          if (!logsMap[l.attendance_id]) logsMap[l.attendance_id] = [];
          logsMap[l.attendance_id].push(l);
        });
      }

      // Combine
      const combined = (attendance || []).map((a: any) => ({
        ...a,
        user: userMap[a.user_id],
        logs: logsMap[a.id] || [],
      }));

      setRecords(combined);
    } catch (e) {
      console.error('Reports error:', e);
    } finally {
      setLoading(false);
    }
  }, [companyId, from, to]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const getCheckIn = (r: any) => {
    const inLog = r.logs?.find((l: any) => l.type === 'check_in');
    const ts = inLog?.timestamp || r.check_in_time;
    return ts ? new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—';
  };

  const getCheckOut = (r: any) => {
    const outLog = [...(r.logs || [])].reverse().find((l: any) => l.type === 'check_out');
    const ts = outLog?.timestamp || r.check_out_time;
    return ts ? new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—';
  };

  const getHours = (r: any) => {
    if (hoursMode === 'sessions') return r.total_hours_sessions || r.total_hours || 0;
    return r.total_hours || 0;
  };

  const getPunches = (r: any) => r.logs?.length || (r.check_in_time ? 1 : 0);

  const exportCSV = () => {
    const headers = ['Date','Employee','Code','Check In','Check Out','Hours','Punches','Status'];
    const rows = records.map(r => [
      r.date, r.user?.full_name || '', r.user?.employee_code || '',
      getCheckIn(r), getCheckOut(r), getHours(r), getPunches(r), r.status
    ]);
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `attendance_${from}_to_${to}.csv`;
    a.click();
  };

  const present = records.filter(r => r.status === 'present').length;
  const absent = records.filter(r => r.status === 'absent').length;
  const late = records.filter(r => r.status === 'late').length;

  return (
    <div className="animate-fadeUp" style={{ padding: 32 }}>
      <PageHeader title="Reports & Export" subtitle="Attendance data with full punch history" action={<Btn onClick={exportCSV}>⬇ Export CSV</Btn>} />

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        {[['From', from, setFrom], ['To', to, setTo]].map(([label, val, setter]: any) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 13, color: 'var(--muted)' }}>{label}</label>
            <input type="date" value={val} onChange={e => setter(e.target.value)}
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', color: 'var(--text)', fontSize: 14, fontFamily: 'var(--font)', outline: 'none' }} />
          </div>
        ))}
        <div style={{ display: 'flex', gap: 4, background: 'var(--surface2)', borderRadius: 8, padding: 4, border: '1px solid var(--border)' }}>
          {[['total', 'First→Last'], ['sessions', 'Sessions']].map(([mode, label]) => (
            <button key={mode} onClick={() => setHoursMode(mode as any)} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontFamily: 'var(--font)', background: hoursMode === mode ? 'var(--accent)' : 'transparent', color: hoursMode === mode ? '#000' : 'var(--muted)', fontWeight: 700, fontSize: 12 }}>{label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>Total: <strong style={{ color: 'var(--text)' }}>{records.length}</strong></span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)' }}>Present: {present}</span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--danger)' }}>Absent: {absent}</span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--warn)' }}>Late: {late}</span>
        </div>
      </div>

      <Card>
        <Table headers={['Date', 'Employee', 'Code', 'Check In', 'Check Out', `Hours (${hoursMode === 'total' ? 'First→Last' : 'Sessions'})`, 'Punches', 'Status']}>
          {loading ? <EmptyRow cols={8} message="Loading..." /> :
           records.length === 0 ? <EmptyRow cols={8} message="No records found. Try adjusting the date range." /> :
           records.map(r => (
            <tr key={r.id}>
              <Td mono>{r.date}</Td>
              <Td>{r.user?.full_name || '—'}</Td>
              <Td mono>{r.user?.employee_code || '—'}</Td>
              <Td mono>{getCheckIn(r)}</Td>
              <Td mono>{getCheckOut(r)}</Td>
              <Td mono><span style={{ color: 'var(--accent)', fontWeight: 700 }}>{getHours(r)}h</span></Td>
              <Td mono>{getPunches(r)}</Td>
              <Td><Badge type={r.status === 'present' ? 'green' : r.status === 'late' ? 'yellow' : 'red'}>{r.status}</Badge></Td>
            </tr>
          ))}
        </Table>
      </Card>
    </div>
  );
}

export function CompanyShifts({ companyId }: { companyId: string }) {
  const [shifts, setShifts] = useState<any[]>([]);
  const [modal, setModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', start_time: '09:00', end_time: '18:00', late_threshold_minutes: '15' });

  const load = useCallback(async () => {
    const { data } = await sb.from('shifts').select('*').eq('company_id', companyId).order('created_at');
    setShifts(data || []);
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name) return;
    setSaving(true);
    await sb.from('shifts').insert({ company_id: companyId, ...form, late_threshold_minutes: parseInt(form.late_threshold_minutes) });
    setSaving(false); setModal(false);
    setForm({ name: '', start_time: '09:00', end_time: '18:00', late_threshold_minutes: '15' });
    load();
  };

  const del = async (id: string) => {
    await sb.from('shifts').delete().eq('id', id); load();
  };

  return (
    <div className="animate-fadeUp" style={{ padding: 32 }}>
      <PageHeader title="Shifts" subtitle="Optional. Enables late detection when assigned to employees." action={<Btn onClick={() => setModal(true)}>+ Add Shift</Btn>} />
      <Card>
        <Table headers={['Shift Name', 'Start', 'End', 'Late After', 'Actions']}>
          {shifts.length === 0 ? <EmptyRow cols={5} message="No shifts. Shifts are optional." /> :
           shifts.map(s => (
            <tr key={s.id}>
              <Td><strong>{s.name}</strong></Td>
              <Td mono>{s.start_time}</Td>
              <Td mono>{s.end_time}</Td>
              <Td mono>{s.late_threshold_minutes} min</Td>
              <Td><Btn variant="danger" size="sm" onClick={() => del(s.id)}>Delete</Btn></Td>
            </tr>
          ))}
        </Table>
      </Card>
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: '#000000cc', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div style={{ background: 'var(--surface)', borderRadius: 20, padding: 28, width: 400, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 20 }}>Add Shift</div>
            <div style={{ display: 'grid', gap: 14 }}>
              <div><label style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', display: 'block', marginBottom: 6 }}>SHIFT NAME</label><input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', fontSize: 14, color: 'var(--text)', outline: 'none' }} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><label style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', display: 'block', marginBottom: 6 }}>START</label><input type="time" value={form.start_time} onChange={e => setForm(f => ({...f, start_time: e.target.value}))} style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', fontSize: 14, color: 'var(--text)', outline: 'none' }} /></div>
                <div><label style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', display: 'block', marginBottom: 6 }}>END</label><input type="time" value={form.end_time} onChange={e => setForm(f => ({...f, end_time: e.target.value}))} style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', fontSize: 14, color: 'var(--text)', outline: 'none' }} /></div>
              </div>
              <div><label style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', display: 'block', marginBottom: 6 }}>LATE AFTER (MIN)</label><input type="number" value={form.late_threshold_minutes} onChange={e => setForm(f => ({...f, late_threshold_minutes: e.target.value}))} style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', fontSize: 14, color: 'var(--text)', outline: 'none' }} /></div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <Btn variant="outline" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Create'}</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function CompanyLocations({ companyId }: { companyId: string }) {
  const [locations, setLocations] = useState<any[]>([]);
  const [modal, setModal] = useState(false);
  const [locating, setLocating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string|null>(null);
  const [form, setForm] = useState({ name: '', address: '', latitude: '', longitude: '', radius_meters: '100' });

  const load = useCallback(async () => {
    const { data } = await sb.from('locations').select('*').eq('company_id', companyId).order('created_at');
    setLocations(data || []);
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const useMyLocation = () => {
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      pos => { setForm(f => ({...f, latitude: pos.coords.latitude.toFixed(6), longitude: pos.coords.longitude.toFixed(6)})); setLocating(false); },
      () => setLocating(false)
    );
  };

  const save = async () => {
    if (!form.name || !form.latitude || !form.longitude) return;
    setSaving(true);
    await sb.from('locations').insert({ company_id: companyId, name: form.name, address: form.address, latitude: parseFloat(form.latitude), longitude: parseFloat(form.longitude), radius_meters: parseInt(form.radius_meters) });
    setSaving(false); setModal(false);
    setForm({ name: '', address: '', latitude: '', longitude: '', radius_meters: '100' });
    load();
  };

  const deleteLocation = async (id: string, name: string) => {
    const { data: assigned } = await sb.from('users').select('full_name').eq('location_id', id);
    const count = assigned?.length || 0;
    if (count > 0 && !window.confirm(`"${name}" is assigned to ${count} employee(s). Deleting will unassign them. Continue?`)) return;
    setDeleting(id);
    if (count > 0) await sb.from('users').update({ location_id: null }).eq('location_id', id);
    await sb.from('locations').delete().eq('id', id);
    setDeleting(null);
    load();
  };

  return (
    <div className="animate-fadeUp" style={{ padding: 32 }}>
      <PageHeader title="Office Locations" subtitle="Set geofence zones for employee check-in" action={<Btn onClick={() => setModal(true)}>+ Add Location</Btn>} />
      <div style={{ background: '#845EC210', border: '1px solid #845EC230', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#845EC2' }}>
        📍 After adding locations, go to Employees → click Assign to set each employee's check-in location.
      </div>
      {locations.length === 0 ? (
        <Card><div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--muted)' }}><div style={{ fontSize: 48, marginBottom: 12 }}>📍</div><div>No locations added yet</div></div></Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 20 }}>
          {locations.map(l => (
            <div key={l.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden' }}>
              <div style={{ height: 180, overflow: 'hidden' }}>
                <iframe title={l.name} width="100%" height="100%" style={{ border: 'none' }} src={`https://www.google.com/maps?q=${l.latitude},${l.longitude}&z=17&output=embed`} loading="lazy" />
              </div>
              <div style={{ padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontWeight: 700 }}>{l.name}</div>
                  <span style={{ background: '#00C9A720', color: 'var(--accent)', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{l.radius_meters}m</span>
                </div>
                {l.address && <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>{l.address}</div>}
                <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)', marginBottom: 12 }}>📌 {parseFloat(l.latitude).toFixed(4)}, {parseFloat(l.longitude).toFixed(4)}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <a href={`https://www.google.com/maps?q=${l.latitude},${l.longitude}`} target="_blank" rel="noreferrer" style={{ flex: 1, textAlign: 'center', background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 8, padding: '8px', fontSize: 12, fontWeight: 600 }}>🗺 Open Maps</a>
                  <Btn variant="danger" size="sm" onClick={() => deleteLocation(l.id, l.name)} disabled={deleting === l.id}>{deleting === l.id ? '...' : 'Delete'}</Btn>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: '#000000cc', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }} onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div style={{ background: 'var(--surface)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 480, border: '1px solid var(--border)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 20 }}>Add Office Location</div>
            <div style={{ display: 'grid', gap: 14 }}>
              {[['Location Name','name','text','Head Office'],['Address (optional)','address','text','123 Main St, Chennai']].map(([label,key,type,placeholder]) => (
                <div key={key}><label style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', display: 'block', marginBottom: 6 }}>{label.toUpperCase()}</label><input type={type as any} value={(form as any)[key]} onChange={e => setForm(f => ({...f, [key]: e.target.value}))} placeholder={placeholder as string} style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', fontSize: 14, color: 'var(--text)', outline: 'none' }} /></div>
              ))}
              <button onClick={useMyLocation} disabled={locating} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 8, padding: '10px', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 14 }}>
                {locating ? '⏳ Getting location...' : '📍 Use My Current Location'}
              </button>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><label style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', display: 'block', marginBottom: 6 }}>LATITUDE</label><input value={form.latitude} onChange={e => setForm(f => ({...f, latitude: e.target.value}))} placeholder="11.0168" style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', fontSize: 14, color: 'var(--text)', outline: 'none' }} /></div>
                <div><label style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', display: 'block', marginBottom: 6 }}>LONGITUDE</label><input value={form.longitude} onChange={e => setForm(f => ({...f, longitude: e.target.value}))} placeholder="76.9558" style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', fontSize: 14, color: 'var(--text)', outline: 'none' }} /></div>
              </div>
              {form.latitude && form.longitude && (
                <div style={{ borderRadius: 10, overflow: 'hidden', height: 160, border: '1px solid var(--border)' }}>
                  <iframe title="preview" width="100%" height="100%" style={{ border: 'none' }} src={`https://www.google.com/maps?q=${form.latitude},${form.longitude}&z=17&output=embed`} loading="lazy" />
                </div>
              )}
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', display: 'block', marginBottom: 6 }}>RADIUS: {form.radius_meters}m</label>
                <input type="range" min="50" max="500" step="10" value={form.radius_meters} onChange={e => setForm(f => ({...f, radius_meters: e.target.value}))} style={{ width: '100%', cursor: 'pointer' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--muted)', marginTop: 4 }}><span>50m (strict)</span><span style={{ color: 'var(--accent)', fontWeight: 700 }}>{form.radius_meters}m</span><span>500m (relaxed)</span></div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <Btn variant="outline" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Location'}</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
